import React from "react";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Button,
  Card,
  Col,
  Collapse,
  Empty,
  Form,
  Input,
  Modal,
  Row,
  Select,
  Space,
  Statistic,
  Switch,
  Tag,
  Typography,
} from "antd";
import { CloseOutlined, PlusOutlined, WarningOutlined } from "@ant-design/icons";

import { ItemSchemaGroup, MenuType } from "../../types/model";
import { zIndex } from "../../config/theme";
import { useAntdTheme, useNotification } from "../../shared/hooks";
import { PointsConfig } from "../points-config";
import { createComboSchema, updateComboSchema } from "../combos/api";
import { ComboAddonModal } from "../combos/ComboAddonModal";
import { ItemPicker, ItemThumb, MoveButtons, tierLabel } from "../combos/components";
import {
  NonRedeemableReason,
  comboForSave,
  computeComboPreview,
  effectiveTierPoints,
  formatCents,
  indexMenuItems,
  moveInArray,
} from "../combos/pricing";
import { ComboAddonSchemaWithUsage, ComboSchema } from "../combos/types";
import { ImageUploadTrigger } from "./ImageUploadCell";

const { Text, Title } = Typography;

export type ComboSchemaModalProps = {
  open: boolean;
  restaurantId: string;
  // The combo to edit, or a template (with item_schema_group_id) to create one.
  combo: ComboSchema;
  menuType: MenuType;
  // Every section of the menu; entrees are picked from those of menuType.
  groups: ReadonlyArray<ItemSchemaGroup>;
  // Every add-on; the picker offers those of menuType.
  addons: ReadonlyArray<ComboAddonSchemaWithUsage>;
  pointsConfig: PointsConfig | undefined;
  onClose: () => void;
  onSaved: (combo: ComboSchema & { id: string }) => void;
  // An add-on was created from inside the modal.
  onAddonCreated: (addon: ComboAddonSchemaWithUsage) => void;
};

export const ComboSchemaModal: React.FC<ComboSchemaModalProps> = (props) => {
  // Mount the editor only while open so every open starts from props.
  return props.open ? <ComboSchemaEditor {...props} /> : null;
};

const ComboSchemaEditor: React.FC<ComboSchemaModalProps> = ({
  restaurantId,
  combo,
  menuType,
  groups,
  addons,
  pointsConfig,
  onClose,
  onSaved,
  onAddonCreated,
}) => {
  const { t } = useTranslation();
  const { token } = useAntdTheme();
  const { createSuccess, updateSuccess, notifyError } = useNotification();

  const [draft, setDraft] = React.useState<ComboSchema>(() => ({
    ...combo,
    entrees: [...(combo.entrees ?? [])].sort((a, b) => a.seq - b.seq),
    addons: [...(combo.addons ?? [])].sort((a, b) => a.seq - b.seq),
  }));
  const [saving, setSaving] = React.useState(false);
  const [triedSave, setTriedSave] = React.useState(false);
  const [addonToAttach, setAddonToAttach] = React.useState<string | undefined>(undefined);
  const [creatingAddon, setCreatingAddon] = React.useState(false);

  const menuGroups = React.useMemo(
    () => groups.filter((g) => g.menu_type === menuType),
    [groups, menuType]
  );
  const items = React.useMemo(() => indexMenuItems(menuGroups), [menuGroups]);
  const menuAddons = addons.filter((a) => a.menu_type === menuType);
  const addonById = new Map(addons.map((a) => [a.id, a]));

  const preview = computeComboPreview(draft, addonById, items, pointsConfig);
  const name = draft.name.trim();

  const setIntlName = (lang: string, value: string) =>
    setDraft({ ...draft, intl_name: { ...draft.intl_name, [lang]: value } });

  const attachAddon = (addonId: string) => {
    if (draft.addons.some((a) => a.combo_addon_schema_id === addonId)) {
      return;
    }
    setDraft((prev) => ({
      ...prev,
      addons: [
        ...prev.addons,
        { combo_addon_schema_id: addonId, required: false, seq: prev.addons.length },
      ],
    }));
  };

  const handleSave = () => {
    setTriedSave(true);
    if (name === "" || draft.entrees.length === 0) {
      return;
    }
    setSaving(true);
    const body = comboForSave(draft);
    const onError = (err: unknown) => {
      setSaving(false);
      notifyError(t("combo_failed_to_save"), err);
    };
    if (combo.id) {
      const id = combo.id;
      updateComboSchema(
        restaurantId,
        id,
        body,
        () => {
          setSaving(false);
          updateSuccess(body.name);
          onSaved({ ...body, id });
        },
        onError
      );
    } else {
      createComboSchema(
        restaurantId,
        body,
        (id) => {
          setSaving(false);
          createSuccess(body.name);
          onSaved({ ...body, id });
        },
        onError
      );
    }
  };

  const reasonText = (r: NonRedeemableReason): string =>
    r.kind === "entree"
      ? t("combo_reason_entree_no_points", { name: r.itemName })
      : t("combo_reason_tier_no_points", { addon: r.addonName, tier: tierLabel(t, r.tier) });

  const cardStyle = {
    borderRadius: token.borderRadiusLG,
    border: `1px solid ${token.colorBorder}`,
  };

  const unattachedAddons = menuAddons.filter(
    (a) => !draft.addons.some((link) => link.combo_addon_schema_id === a.id)
  );

  return (
    <Modal
      open
      title={
        <Title level={4} style={{ margin: 0 }}>
          {combo.id ? combo.name : t("combo_create")}
        </Title>
      }
      onCancel={onClose}
      zIndex={zIndex.modal}
      width="calc(100vw - 48px)"
      style={{ top: 24 }}
      styles={{
        body: { maxHeight: "calc(100vh - 160px)", overflowY: "auto", padding: token.paddingXL },
      }}
      footer={
        <Space>
          <Button onClick={onClose}>{t("cancel")}</Button>
          <Button type="primary" loading={saving} onClick={handleSave}>
            {t("save")}
          </Button>
        </Space>
      }
    >
      <Row gutter={[token.marginLG, token.marginLG]}>
        <Col xs={24} lg={16}>
          <Card title={t("combo_basic_info")} style={cardStyle}>
            <Form layout="vertical" colon={false} component="div">
              <Row gutter={token.marginMD}>
                <Col xs={24} md={16}>
                  <Form.Item
                    label={t("name")}
                    required
                    validateStatus={triedSave && name === "" ? "error" : undefined}
                    help={triedSave && name === "" ? t("name_is_required") : undefined}
                  >
                    <Input
                      aria-label={t("combo_name")}
                      value={draft.name}
                      onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                    />
                  </Form.Item>
                  <Row gutter={token.marginMD}>
                    <Col xs={24} md={8}>
                      <Form.Item label={t("combo_chinese_name")}>
                        <Input
                          value={draft.intl_name?.zh ?? ""}
                          onChange={(e) => setIntlName("zh", e.target.value)}
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                      <Form.Item label={t("combo_spanish_name")}>
                        <Input
                          value={draft.intl_name?.es ?? ""}
                          onChange={(e) => setIntlName("es", e.target.value)}
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                      <Form.Item label={t("combo_english_name")}>
                        <Input
                          value={draft.intl_name?.en ?? ""}
                          onChange={(e) => setIntlName("en", e.target.value)}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Form.Item label={t("notes")}>
                    <Input.TextArea
                      autoSize={{ minRows: 2, maxRows: 4 }}
                      value={draft.notes}
                      onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item label={t("image")}>
                    <ImageUploadTrigger
                      restaurantId={restaurantId}
                      imageUrl={draft.image_manifest?.default.url}
                      alt={draft.name}
                      width={180}
                      height={120}
                      onUploaded={(manifest) => {
                        setDraft((prev) => ({ ...prev, image_manifest: manifest }));
                        return Promise.resolve();
                      }}
                    />
                  </Form.Item>
                  <Space size={token.marginLG}>
                    <Form.Item label={t("sold_out")}>
                      <Switch
                        aria-label={t("sold_out")}
                        checked={draft.sold_out}
                        onChange={(checked) => setDraft({ ...draft, sold_out: checked })}
                      />
                    </Form.Item>
                    <Form.Item label={t("featured")}>
                      <Switch
                        aria-label={t("featured")}
                        checked={draft.featured}
                        onChange={(checked) => setDraft({ ...draft, featured: checked })}
                      />
                    </Form.Item>
                  </Space>
                </Col>
              </Row>
            </Form>
          </Card>

          <Card
            title={t("combo_entrees")}
            style={{ ...cardStyle, marginTop: token.marginLG }}
            data-testid="combo-entrees"
          >
            <Text type="secondary" style={{ display: "block", marginBottom: token.marginSM }}>
              {t("combo_entrees_help")}
            </Text>
            <ItemPicker
              groups={menuGroups}
              value={draft.entrees.map((e) => e.item_schema_id)}
              onChange={(ids) =>
                setDraft({
                  ...draft,
                  entrees: ids.map((id, seq) => ({ item_schema_id: id, seq })),
                })
              }
              testId="combo-entree-picker"
            />
            {triedSave && draft.entrees.length === 0 ? (
              <Text type="danger" style={{ display: "block", marginTop: token.marginXS }}>
                {t("combo_needs_entree")}
              </Text>
            ) : null}
            <div style={{ marginTop: token.marginSM }}>
              {draft.entrees.map((e, i) => {
                const ref = items.get(e.item_schema_id);
                return (
                  <div
                    key={e.item_schema_id}
                    data-testid="combo-entree"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: token.marginSM,
                      padding: `${token.paddingXXS}px 0`,
                      borderBottom: `1px solid ${token.colorBorderSecondary}`,
                    }}
                  >
                    <ItemThumb item={ref?.item} />
                    <Text style={{ flex: 1 }} type={ref ? undefined : "danger"}>
                      {ref ? ref.item.name : t("combo_item_not_on_menu")}
                    </Text>
                    {ref ? <Text type="secondary">{formatCents(ref.item.price)}</Text> : null}
                    <MoveButtons
                      index={i}
                      count={draft.entrees.length}
                      onMove={(from, to) =>
                        setDraft({ ...draft, entrees: moveInArray(draft.entrees, from, to) })
                      }
                    />
                    <Button
                      type="text"
                      size="small"
                      aria-label={t("remove")}
                      icon={<CloseOutlined />}
                      onClick={() =>
                        setDraft({
                          ...draft,
                          entrees: draft.entrees.filter(
                            (x) => x.item_schema_id !== e.item_schema_id
                          ),
                        })
                      }
                    />
                  </div>
                );
              })}
            </div>
          </Card>

          <Card
            title={t("combo_addons")}
            style={{ ...cardStyle, marginTop: token.marginLG }}
            data-testid="combo-addons"
          >
            <Space wrap style={{ marginBottom: token.marginSM }}>
              <Select<string>
                aria-label={t("combo_attach_addon")}
                style={{ minWidth: 260 }}
                placeholder={t("combo_attach_addon")}
                value={addonToAttach}
                onChange={(id) => {
                  attachAddon(id);
                  setAddonToAttach(undefined);
                }}
                showSearch
                optionFilterProp="label"
                options={unattachedAddons.map((a) => ({ value: a.id, label: a.name }))}
                notFoundContent={t("combo_no_addons")}
              />
              <Button icon={<PlusOutlined />} onClick={() => setCreatingAddon(true)}>
                {t("combo_create_new_addon")}
              </Button>
            </Space>
            {draft.addons.length === 0 ? (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t("combo_no_addons_yet")} />
            ) : (
              draft.addons.map((link, i) => {
                const addon = addonById.get(link.combo_addon_schema_id);
                const tiers = [...(addon?.tiers ?? [])].sort((a, b) => a.seq - b.seq);
                return (
                  <Card
                    key={link.combo_addon_schema_id}
                    size="small"
                    data-testid="combo-addon"
                    style={{ marginBottom: token.marginSM, borderRadius: token.borderRadiusLG }}
                    title={
                      <Space>
                        <Text strong>{addon?.name ?? link.combo_addon_schema_id}</Text>
                        {link.required ? <Tag color="blue">{t("combo_required")}</Tag> : null}
                      </Space>
                    }
                    extra={
                      <Space size={token.marginXS}>
                        <Text type="secondary">{t("combo_required")}</Text>
                        <Switch
                          size="small"
                          aria-label={t("combo_required_for", { name: addon?.name ?? "" })}
                          checked={link.required}
                          onChange={(checked) =>
                            setDraft({
                              ...draft,
                              addons: draft.addons.map((x) =>
                                x.combo_addon_schema_id === link.combo_addon_schema_id
                                  ? { ...x, required: checked }
                                  : x
                              ),
                            })
                          }
                        />
                        <MoveButtons
                          index={i}
                          count={draft.addons.length}
                          onMove={(from, to) =>
                            setDraft({ ...draft, addons: moveInArray(draft.addons, from, to) })
                          }
                        />
                        <Button
                          type="text"
                          size="small"
                          aria-label={t("remove")}
                          icon={<CloseOutlined />}
                          onClick={() =>
                            setDraft({
                              ...draft,
                              addons: draft.addons.filter(
                                (x) => x.combo_addon_schema_id !== link.combo_addon_schema_id
                              ),
                            })
                          }
                        />
                      </Space>
                    }
                  >
                    <Collapse
                      ghost
                      size="small"
                      items={[
                        {
                          key: "tiers",
                          label: t("combo_tiers_count", { count: tiers.length }),
                          children: tiers.map((tier) => {
                            const pts = effectiveTierPoints(tier, pointsConfig);
                            return (
                              <div key={tier.id} style={{ marginBottom: token.marginXS }}>
                                <Text strong>{tierLabel(t, tier)}</Text>{" "}
                                <Text>{formatCents(tier.price)}</Text>{" "}
                                <Text type="secondary">
                                  {pts !== undefined
                                    ? t("combo_n_pts", { points: pts })
                                    : t("combo_not_redeemable")}
                                </Text>
                                <div>
                                  <Text type="secondary">
                                    {(tier.items ?? [])
                                      .map((ti) => items.get(ti.item_schema_id)?.item.name)
                                      .filter((n): n is string => !!n)
                                      .join(", ")}
                                  </Text>
                                </div>
                              </div>
                            );
                          }),
                        },
                      ]}
                    />
                  </Card>
                );
              })
            )}
          </Card>
        </Col>

        <Col xs={24} lg={8}>
          <Card
            title={t("combo_preview")}
            style={{ ...cardStyle, position: "sticky", top: 0 }}
            data-testid="combo-preview"
          >
            {preview.startingPrice === null ? (
              <Text type="secondary">{t("combo_preview_needs_entree")}</Text>
            ) : (
              <Space direction="vertical" style={{ width: "100%" }}>
                <Statistic
                  title={t("combo_starting_price")}
                  value={t("combo_from_price", { price: formatCents(preview.startingPrice) })}
                />
                <Text data-testid="combo-price-range">
                  {t("combo_price_range", {
                    min: formatCents(preview.startingPrice),
                    max: formatCents(preview.maxPrice ?? preview.startingPrice),
                  })}
                </Text>
                <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
                  {t("combo_price_range_help")}
                </Text>
                {preview.startingPoints !== null ? (
                  <Text strong data-testid="combo-starting-points">
                    {t("combo_from_points", { points: preview.startingPoints })}
                  </Text>
                ) : null}
                {preview.startingPoints === null || preview.nonRedeemable.length > 0 ? (
                  <Alert
                    type="warning"
                    showIcon
                    icon={<WarningOutlined />}
                    data-testid="combo-points-warning"
                    message={
                      preview.startingPoints === null
                        ? t("combo_not_redeemable_with_points")
                        : t("combo_partly_redeemable_with_points")
                    }
                    description={
                      preview.nonRedeemable.length > 0
                        ? preview.nonRedeemable.map(reasonText).join("; ")
                        : undefined
                    }
                  />
                ) : null}
              </Space>
            )}
          </Card>
        </Col>
      </Row>

      <ComboAddonModal
        open={creatingAddon}
        nested
        restaurantId={restaurantId}
        defaultMenuType={menuType}
        lockMenuType
        groups={groups}
        pointsConfig={pointsConfig}
        onClose={() => setCreatingAddon(false)}
        onSaved={(addon) => {
          setCreatingAddon(false);
          onAddonCreated(addon);
          attachAddon(addon.id);
        }}
      />
    </Modal>
  );
};
